/**
 * Gewohnheiten: Streaks und Kennzahlen.
 *
 * Streaks werden IMMER berechnet und NIE gespeichert. Gespeicherte Streaks
 * driften unweigerlich auseinander, sobald ein Eintrag nachtraeglich geaendert,
 * eine Sicherung eingespielt oder der Rhythmus umgestellt wird.
 *
 * Die wichtigste Design-Entscheidung ist die Schonfrist: Der heutige Tag
 * bricht einen Streak NICHT, solange er noch laeuft. Wer morgens um acht in
 * die App schaut, soll nicht sehen, dass seine 40-Tage-Serie gerissen ist,
 * nur weil er heute noch nicht gelaufen ist.
 */
import { addDays, differenceInDays, startOfWeek, weekday } from '@/lib/datetime';
import type { HabitCadence, LocalDate } from '@/domain/types';

/** Wie weit zurueck ueberhaupt gesucht wird - Schutz gegen Endlosschleifen. */
const MAX_LOOKBACK_DAYS = 3650;

export interface StreakResult {
  /** Laufende Serie, einschliesslich der Schonfrist fuer heute. */
  current: number;
  /** Laengste je erreichte Serie. */
  longest: number;
  /** Steht die Gewohnheit heute an? */
  dueToday: boolean;
  /** Ist sie heute schon erledigt? */
  doneToday: boolean;
}

/** Steht die Gewohnheit an diesem Tag an? */
export function isScheduled(date: LocalDate, cadence: HabitCadence): boolean {
  switch (cadence.type) {
    case 'taeglich':
      return true;
    case 'wochentage':
      return cadence.days.includes(weekday(date));
    case 'x_pro_woche':
      // Ein Wochenziel legt keinen bestimmten Tag fest.
      return true;
  }
}

/**
 * Berechnet die Serie.
 *
 * `doneDates` enthaelt die Tage, an denen die Gewohnheit erledigt wurde.
 */
export function computeStreak(
  doneDates: Iterable<LocalDate>,
  cadence: HabitCadence,
  today: LocalDate,
): StreakResult {
  const done = doneDates instanceof Set ? doneDates : new Set(doneDates);

  const doneToday = done.has(today);
  const dueToday = isScheduled(today, cadence);

  if (cadence.type === 'x_pro_woche') {
    return { ...weeklyStreak(done, cadence.count, today), dueToday, doneToday };
  }

  return { ...dailyStreak(done, cadence, today), dueToday, doneToday };
}

/**
 * Serien fuer taegliche Gewohnheiten und feste Wochentage.
 *
 * Gezaehlt werden nur die Tage, an denen die Gewohnheit ueberhaupt ansteht.
 * Ein unerledigter Montag reisst die Serie; ein uebersprungener Sonntag bei
 * "nur werktags" nicht.
 */
function dailyStreak(
  done: Set<LocalDate>,
  cadence: HabitCadence,
  today: LocalDate,
): { current: number; longest: number } {
  // Kein einziger Eintrag: nichts zu zaehlen.
  if (done.size === 0) return { current: 0, longest: 0 };

  let current = 0;
  let cursor = today;

  // Schonfrist: Steht heute an und ist noch offen, beginnt die Zaehlung gestern.
  if (isScheduled(today, cadence) && !done.has(today)) {
    cursor = addDays(today, -1);
  }

  for (let i = 0; i < MAX_LOOKBACK_DAYS; i += 1) {
    if (!isScheduled(cursor, cadence)) {
      cursor = addDays(cursor, -1);
      continue;
    }
    if (!done.has(cursor)) break;
    current += 1;
    cursor = addDays(cursor, -1);
  }

  return { current, longest: Math.max(current, longestDaily(done, cadence)) };
}

/** Laengste je erreichte Serie - laeuft ueber die vorhandenen Eintraege. */
function longestDaily(done: Set<LocalDate>, cadence: HabitCadence): number {
  const sorted = [...done].sort();
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (!first || !last) return 0;

  let longest = 0;
  let run = 0;
  let cursor = first;

  const span = Math.min(differenceInDays(first, last), MAX_LOOKBACK_DAYS);
  for (let i = 0; i <= span; i += 1) {
    if (isScheduled(cursor, cadence)) {
      if (done.has(cursor)) {
        run += 1;
        longest = Math.max(longest, run);
      } else {
        run = 0;
      }
    }
    cursor = addDays(cursor, 1);
  }

  return longest;
}

/**
 * Serien fuer Wochenziele ("dreimal pro Woche").
 *
 * Gezaehlt werden aufeinanderfolgende Wochen, in denen das Ziel erreicht wurde.
 * Die laufende Woche bekommt dieselbe Schonfrist wie der heutige Tag: Ist das
 * Ziel noch nicht erreicht, bricht das die Serie nicht - es zaehlt nur noch
 * nicht mit.
 */
function weeklyStreak(
  done: Set<LocalDate>,
  target: number,
  today: LocalDate,
): { current: number; longest: number } {
  if (done.size === 0 || target <= 0) return { current: 0, longest: 0 };

  const countIn = (weekStart: LocalDate) => {
    let n = 0;
    for (let i = 0; i < 7; i += 1) {
      if (done.has(addDays(weekStart, i))) n += 1;
    }
    return n;
  };

  let current = 0;
  let cursor = startOfWeek(today);

  // Schonfrist fuer die laufende Woche.
  if (countIn(cursor) < target) {
    cursor = addDays(cursor, -7);
  }

  for (let i = 0; i < MAX_LOOKBACK_DAYS / 7; i += 1) {
    if (countIn(cursor) < target) break;
    current += 1;
    cursor = addDays(cursor, -7);
  }

  // Laengste Serie ueber alle Wochen mit Eintraegen.
  const sorted = [...done].sort();
  let longest = 0;
  let run = 0;
  let week = startOfWeek(sorted[0]!);
  const lastWeek = startOfWeek(sorted[sorted.length - 1]!);

  while (week <= lastWeek) {
    if (countIn(week) >= target) {
      run += 1;
      longest = Math.max(longest, run);
    } else {
      run = 0;
    }
    week = addDays(week, 7);
  }

  return { current, longest: Math.max(current, longest) };
}

/**
 * Erfuellungsquote ueber die letzten `days` Tage.
 *
 * Bezugsgroesse sind die Tage, an denen die Gewohnheit anstand - bei "nur
 * werktags" also nicht 30, sondern etwa 22.
 */
export function completionRate(
  doneDates: Iterable<LocalDate>,
  cadence: HabitCadence,
  today: LocalDate,
  days = 30,
): { done: number; due: number; percent: number } {
  const done = doneDates instanceof Set ? doneDates : new Set(doneDates);

  let doneCount = 0;
  let dueCount = 0;

  if (cadence.type === 'x_pro_woche') {
    // Beim Wochenziel ist die Bezugsgroesse das Soll pro Woche.
    for (let i = 0; i < days; i += 1) {
      if (done.has(addDays(today, -i))) doneCount += 1;
    }
    dueCount = Math.round((days / 7) * cadence.count);
  } else {
    for (let i = 0; i < days; i += 1) {
      const date = addDays(today, -i);
      if (!isScheduled(date, cadence)) continue;
      dueCount += 1;
      if (done.has(date)) doneCount += 1;
    }
  }

  return {
    done: doneCount,
    due: dueCount,
    percent: dueCount === 0 ? 0 : Math.round((doneCount / dueCount) * 100),
  };
}
